import React, { useState, useEffect } from 'react';
import { Card, Tabs, Table, Statistic, Typography, Button, Input, Switch, Space, message, Tag, Tooltip, Empty, Descriptions, Spin, Progress, Alert, Row, Col, Pagination } from 'antd';
import { CopyOutlined, GiftOutlined, BellOutlined, WalletOutlined, LinkOutlined, TrophyOutlined, UserOutlined, CrownOutlined, CheckCircleOutlined, CloseCircleOutlined, MedicineBoxOutlined, KeyOutlined, EditOutlined, ClockCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { coinAPI, taskAPI, subscriptionAPI, drugAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import type { ValidationTask } from '../types';
import { formatCN } from '../utils/dateFormat';
import { useResponsive } from '../contexts/ResponsiveContext';

const { Text } = Typography;

const typeMap: Record<string, { color: string; text: string }> = {
  register_bonus: { color: 'green', text: '注册赠送' },
  invite_reward: { color: 'blue', text: '邀请奖励' },
  daily_login: { color: 'gold', text: '每日登录' },
  exchange: { color: 'orange', text: '金币兑换' },
  admin_adjust: { color: 'purple', text: '管理员调整' },
  task_reward: { color: 'cyan', text: '任务奖励' },
};

const PersonalAccount: React.FC = () => {
  const [balance, setBalance] = useState(0);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteCount, setInviteCount] = useState(0);
  const [referralFunnel, setReferralFunnel] = useState<any>(null);
  const [config, setConfig] = useState({ register_bonus: 0, invite_reward: 0, daily_login_reward: 0 });
  const [history, setHistory] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [emailNotify, setEmailNotify] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [savingNotify, setSavingNotify] = useState(false);
  const [myTasks, setMyTasks] = useState<ValidationTask[]>([]);
  const [myTasksLoading, setMyTasksLoading] = useState(false);
  const [planInfo, setPlanInfo] = useState<any>(null);
  const [allSubs, setAllSubs] = useState<any[]>([]);
  const [features, setFeatures] = useState<Record<string, boolean>>({});
  const [limits, setLimits] = useState<Record<string, number>>({});
  const [planLoading, setPlanLoading] = useState(true);
  const [activatedDrugCount, setActivatedDrugCount] = useState(0);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isMobileShell, isPhone } = useResponsive();

  const fetchBalance = async () => {
    try {
      const res = await coinAPI.getBalance();
      setBalance(res.balance);
      setInviteCode(res.invite_code);
      setInviteCount(res.invite_count);
      setConfig(res.config);
    } catch { /* ignore */ }
  };

  const fetchReferralFunnel = async () => {
    try {
      setReferralFunnel(await coinAPI.getReferralFunnel());
    } catch { /* analytics are optional on the account page */ }
  };

  const fetchHistory = async (p: number) => {
    setLoading(true);
    try {
      const res = await coinAPI.getHistory(p, 10);
      setHistory(res.items);
      setTotal(res.total);
      setPage(res.page);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const fetchNotification = async () => {
    try {
      const res = await coinAPI.getNotificationSettings();
      setEmailNotify(res.email_notify);
      setWebhookUrl(res.webhook_url || '');
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchBalance(); fetchReferralFunnel(); fetchHistory(1); fetchNotification(); }, []);

  useEffect(() => {
    setPlanLoading(true);
    Promise.all([
      subscriptionAPI.getCurrent().catch(() => null),
      subscriptionAPI.getFeatures().catch(() => ({ features: {}, limits: {} })),
      drugAPI.activationStatus().catch(() => null),
      subscriptionAPI.getAll().catch(() => []),
    ]).then(([sub, fl, drugStatus, subs]) => {
      setPlanInfo(sub);
      setAllSubs(subs || []);
      setFeatures(fl.features || {});
      setLimits(fl.limits || {});
      if (drugStatus) {
        setActivatedDrugCount(drugStatus.activated_ids?.length || 0);
      }
    }).finally(() => setPlanLoading(false));
  }, []);

  const fetchMyTasks = async () => {
    setMyTasksLoading(true);
    try {
      const res = await taskAPI.myTasks();
      setMyTasks(res.tasks || []);
    } catch { /* ignore */ }
    setMyTasksLoading(false);
  };

  const copy = (text: string) => {
    navigator.clipboard.writeText(text).then(() => message.success('已复制'));
  };

  const saveNotification = async () => {
    setSavingNotify(true);
    try {
      await coinAPI.updateNotificationSettings({ email_notify: emailNotify, webhook_url: webhookUrl });
      message.success('保存成功');
    } catch { message.error('保存失败'); }
    setSavingNotify(false);
  };

  const columns = [
    { title: '时间', dataIndex: 'created_at', key: 'created_at', width: 180,
      render: (v: string) => formatCN(v) },
    {
      title: '类型', dataIndex: 'type', key: 'type', width: 120,
      render: (t: string) => {
        const m = typeMap[t];
        return m ? <Tag color={m.color}>{m.text}</Tag> : <Tag>{t}</Tag>;
      },
    },
    {
      title: '金额', dataIndex: 'amount', key: 'amount', width: 100,
      render: (v: number) => <Text type={v >= 0 ? 'success' : 'danger'}>{v >= 0 ? `+${v}` : v}</Text>,
    },
    { title: '余额', dataIndex: 'balance_after', key: 'balance_after', width: 100 },
    { title: '说明', dataIndex: 'description', key: 'description' },
  ];

  const inviteLink = `${window.location.origin}/register?invite=${inviteCode}`;
  const compactTabLabelStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6 };

  const tabLabel = (icon: React.ReactNode, desktopLabel: string, mobileLabel: string) => (
    <span style={compactTabLabelStyle}>
      {icon}
      <span>{isPhone ? mobileLabel : desktopLabel}</span>
    </span>
  );

  const renderCoinHistoryCards = () => (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {history.length === 0 && !loading ? (
        <Empty description="暂无金币流水" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        history.map((item) => {
          const typeMeta = typeMap[item.type] || { color: 'default', text: item.type };
          return (
            <Card key={item.id} size="small" bodyStyle={{ padding: 14, borderRadius: 16 }}>
              <Space direction="vertical" size={10} style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <Space direction="vertical" size={4} style={{ minWidth: 0 }}>
                    <Tag color={typeMeta.color} style={{ width: 'fit-content', marginInlineEnd: 0 }}>{typeMeta.text}</Tag>
                    <Text strong style={{ fontSize: 15 }}>
                      {item.amount >= 0 ? `+${item.amount}` : item.amount} 金币
                    </Text>
                  </Space>
                  <Text type="secondary" style={{ fontSize: 12, textAlign: 'right' }}>
                    {formatCN(item.created_at, 'MM-DD HH:mm')}
                  </Text>
                </div>
                <Row gutter={[12, 8]}>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 12 }}>变动后余额</Text>
                    <div style={{ fontWeight: 600, color: '#1f2937' }}>{item.balance_after ?? '-'}</div>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 12 }}>说明</Text>
                    <div style={{ fontWeight: 500, color: '#1f2937', wordBreak: 'break-word' }}>{item.description || '-'}</div>
                  </Col>
                </Row>
              </Space>
            </Card>
          );
        })
      )}
      {total > 10 && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Pagination
            current={page}
            total={total}
            pageSize={10}
            size="small"
            onChange={(p) => fetchHistory(p)}
            showSizeChanger={false}
          />
        </div>
      )}
    </Space>
  );

  const renderInviteStatsCards = () => {
    const statItems = [
      { title: '已邀请人数', value: inviteCount, suffix: '人' },
      { title: '每次邀请奖励', value: config.invite_reward, suffix: '金币' },
      { title: '注册赠送', value: config.register_bonus, suffix: '金币' },
      ...(config.daily_login_reward > 0 ? [{ title: '每日登录奖励', value: config.daily_login_reward, suffix: '金币' }] : []),
    ];

    return (
      <Row gutter={[12, 12]}>
        {statItems.map((item) => (
          <Col xs={12} key={item.title}>
            <Card size="small" bodyStyle={{ padding: isPhone ? 12 : 16, borderRadius: 16 }}>
              <Statistic
                title={item.title}
                value={item.value}
                suffix={item.suffix}
                valueStyle={{ fontSize: isPhone ? 20 : 24, color: '#1f2937' }}
              />
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  const renderReferralFunnel = () => {
    const counts = referralFunnel?.counts || {};
    const totalInvites = Number(counts.registered || inviteCount || 0);
    const steps = [
      { key: 'email_verified', label: '邮箱验证', color: '#1677ff' },
      { key: 'tenant_joined', label: '加入机构', color: '#52c41a' },
      { key: 'first_case_created', label: '创建首个病例', color: '#fa8c16' },
      { key: 'first_calculation_completed', label: '完成首次计算', color: '#722ed1' },
    ];
    return (
      <Card size="small" title="邀请转化漏斗" bodyStyle={{ padding: isPhone ? 12 : 16 }}>
        {totalInvites === 0 ? (
          <Text type="secondary">暂无可统计的邀请用户</Text>
        ) : (
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            {steps.map((step) => {
              const value = Number(counts[step.key] || 0);
              return (
                <div key={step.key}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <Text>{step.label}</Text>
                    <Text strong>{value} / {totalInvites}</Text>
                  </div>
                  <Progress percent={Math.min(100, Math.round((value / totalInvites) * 100))} strokeColor={step.color} showInfo={false} />
                </div>
              );
            })}
          </Space>
        )}
      </Card>
    );
  };

  const FEATURE_LABELS: Record<string, string> = {
    ai_assistant: 'AI智能助手',
    report_export: '报告导出',
    tdm_optimize: 'TDM剂量优化',
    multi_drug: '多药物支持',
    custom_models: '自定义模型',
    api_access: 'API接口',
  };

  const LIMIT_LABELS: Record<string, string> = {
    max_patients_month: '每月最大患者数',
    max_patients: '每月最大患者数',
    max_calculations_month: '每月计算次数',
    max_members: '机构成员数',
    max_models: '模型数',
  };

  // 药物激活配额：根据 multi_drug 特性动态计算
  const multiDrugEnabled = features.multi_drug;
  const drugLimit = multiDrugEnabled ? (limits.max_drugs || 0) : 1;
  const drugLimitLabel = drugLimit === -1 ? '无限' : `${drugLimit}`;
  const drugQuotaUsedUp = drugLimit !== -1 && drugLimit > 0 && activatedDrugCount >= drugLimit;

  const tabItems = [
    {
      key: 'account',
      label: tabLabel(<UserOutlined />, '个人账户', '账户'),
      children: planLoading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Card title="账户信息" size="small" extra={<Button size="small" icon={<EditOutlined />} onClick={() => navigate('/settings/account')}>编辑</Button>}>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="用户名">{user?.username || user?.email?.split('@')[0] || '-'}</Descriptions.Item>
              <Descriptions.Item label="邮箱">{user?.email || '-'}</Descriptions.Item>
              <Descriptions.Item label="真实姓名">{user?.real_name || '-'}</Descriptions.Item>
              <Descriptions.Item label="手机号">{user?.phone || '-'}</Descriptions.Item>
            </Descriptions>
          </Card>
          <Card
            title={<span><CrownOutlined style={{ marginRight: 6, color: '#C5A55A' }} />当前套餐</span>}
            size="small"
          >
            <Descriptions column={2} size="small">
              <Descriptions.Item label="生效套餐">
                <Tag color="blue">{planInfo?.plan_display_name || '免费版'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={planInfo?.status === 'active' ? 'green' : 'default'}>
                  {planInfo?.status === 'active' ? '生效中' : '未订阅'}
                </Tag>
              </Descriptions.Item>
              {planInfo?.current_period_end && (
                <Descriptions.Item label="到期时间">
                  {formatCN(planInfo.current_period_end, 'YYYY-MM-DD')}
                </Descriptions.Item>
              )}
            </Descriptions>
            {allSubs.length > 1 && (
              <div style={{ marginTop: 12 }}>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                  您拥有 {allSubs.length} 个活跃订阅，系统自动使用最高级别套餐：
                </Text>
                <Space wrap>
                  {allSubs.map((s: any) => (
                    <Tag
                      key={s.id}
                      color={s.is_effective ? 'blue' : 'default'}
                      icon={s.is_effective ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                    >
                      {s.plan_display_name}（至 {formatCN(s.current_period_end, 'YYYY-MM-DD')}）
                    </Tag>
                  ))}
                </Space>
                <Alert
                  type="info"
                  showIcon
                  icon={<InfoCircleOutlined />}
                  style={{ marginTop: 8 }}
                  message="高级套餐到期后，系统将自动降级到剩余的最高级别套餐，无需手动操作。"
                />
              </div>
            )}
          </Card>
          <Card title="功能权限" size="small">
            <Space wrap>
              {Object.entries(FEATURE_LABELS).map(([key, label]) => (
                <Tag
                  key={key}
                  color={features[key] ? 'green' : 'default'}
                  icon={features[key] ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                >
                  {label}
                </Tag>
              ))}
            </Space>
          </Card>
          <Card title="使用限制" size="small">
            <Descriptions column={2} size="small">
              {Object.entries(LIMIT_LABELS).map(([key, label]) => (
                <Descriptions.Item key={key} label={label}>
                  {limits[key] === -1 ? '无限' : limits[key] ?? 0}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </Card>
          <Card
            title={<span><MedicineBoxOutlined style={{ marginRight: 6 }} />药物激活配额</span>}
            size="small"
          >
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <Descriptions column={2} size="small">
                <Descriptions.Item label="多药物支持">
                  <Tag color={multiDrugEnabled ? 'green' : 'default'} icon={multiDrugEnabled ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                    {multiDrugEnabled ? '已开启' : '未开启'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="可激活上限">
                  {drugLimitLabel}
                </Descriptions.Item>
                <Descriptions.Item label="已激活药物数">
                  <Text strong type={drugQuotaUsedUp ? 'warning' : undefined}>
                    {activatedDrugCount}
                  </Text>
                  <Text type="secondary"> / {drugLimitLabel}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="剩余配额">
                  {drugLimit === -1 ? (
                    <Tag color="green">无限</Tag>
                  ) : drugQuotaUsedUp ? (
                    <Tag color="red">{activatedDrugCount > drugLimit ? `已超额 ${activatedDrugCount - drugLimit}` : '已用完'}</Tag>
                  ) : (
                    <Tag color="blue">{Math.max(0, drugLimit - activatedDrugCount)}</Tag>
                  )}
                </Descriptions.Item>
              </Descriptions>
              {drugLimit > 0 && drugLimit !== -1 && (
                <Progress
                  percent={Math.min(100, Math.round((activatedDrugCount / drugLimit) * 100))}
                  status={drugQuotaUsedUp ? 'exception' : 'active'}
                  format={() => `${activatedDrugCount}/${drugLimit}`}
                  size="small"
                />
              )}
              {!multiDrugEnabled && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  当前套餐仅支持激活1种药物，升级套餐可开启多药物支持
                </Text>
              )}
            </Space>
          </Card>
        </Space>
      ),
    },
    {
      key: 'coin',
      label: tabLabel(<WalletOutlined />, '金币账户', '金币'),
      children: (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Card
            style={{
              marginBottom: isMobileShell ? 0 : 16,
              textAlign: 'center',
              borderRadius: 20,
              background: 'linear-gradient(135deg, #fff8e7 0%, #fff 100%)',
              border: '1px solid rgba(197,165,90,0.22)',
            }}
            bodyStyle={{ padding: isPhone ? '20px 16px' : '24px 20px' }}
          >
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Statistic
                title="当前余额"
                value={balance}
                suffix="金币"
                valueStyle={{ color: '#C5A55A', fontSize: isPhone ? 28 : 36 }}
              />
              <Text type="secondary" style={{ fontSize: 13 }}>
                可用于兑换激活码、参与平台奖励活动
              </Text>
              <Button
                type={isMobileShell ? 'primary' : 'link'}
                icon={<KeyOutlined />}
                onClick={() => window.location.href = '/activation'}
                block={isMobileShell}
                style={isMobileShell ? { marginTop: 4 } : { marginTop: 8 }}
              >
                金币兑换激活码
              </Button>
            </Space>
          </Card>

          <Card
            title="金币流水"
            size="small"
            bodyStyle={{ padding: isMobileShell ? 12 : 16 }}
          >
            {isMobileShell ? renderCoinHistoryCards() : (
              <Table
                columns={columns}
                dataSource={history}
                rowKey="id"
                loading={loading}
                pagination={{ current: page, total, pageSize: 10, onChange: (p) => fetchHistory(p) }}
                size="small"
              />
            )}
          </Card>
        </Space>
      ),
    },
    {
      key: 'invite',
      label: tabLabel(<GiftOutlined />, '邀请奖励', '邀请'),
      children: (
        <Card bodyStyle={{ padding: isPhone ? 14 : 24 }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Alert
              type="info"
              showIcon
              message={`好友通过你的邀请码注册后，你可获得 ${config.invite_reward} 金币奖励`}
            />

            <Card
              size="small"
              bodyStyle={{ padding: isPhone ? 14 : 18, borderRadius: 18 }}
              style={{ borderRadius: 18 }}
            >
              <Space direction="vertical" size={10} style={{ width: '100%' }}>
                <Text type="secondary">我的邀请码</Text>
                <div
                  style={{
                    borderRadius: 16,
                    background: '#f8fafc',
                    border: '1px solid #e5e7eb',
                    padding: isPhone ? '12px 14px' : '14px 16px',
                    display: 'flex',
                    alignItems: isPhone ? 'stretch' : 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    flexDirection: isPhone ? 'column' : 'row',
                  }}
                >
                  <Text strong copyable={false} style={{ fontSize: isPhone ? 22 : 24, letterSpacing: 1, wordBreak: 'break-all' }}>
                    {inviteCode || '-'}
                  </Text>
                  <Tooltip title="复制邀请码">
                    <Button icon={<CopyOutlined />} size={isPhone ? 'middle' : 'small'} onClick={() => copy(inviteCode)} block={isPhone}>
                      复制邀请码
                    </Button>
                  </Tooltip>
                </div>
              </Space>
            </Card>

            <Card
              size="small"
              bodyStyle={{ padding: isPhone ? 14 : 18, borderRadius: 18 }}
              style={{ borderRadius: 18 }}
            >
              <Space direction="vertical" size={10} style={{ width: '100%' }}>
                <Text type="secondary">邀请链接</Text>
                <Input
                  value={inviteLink}
                  readOnly
                  prefix={<LinkOutlined />}
                  style={{ width: '100%' }}
                />
                <Button icon={<CopyOutlined />} onClick={() => copy(inviteLink)} block={isMobileShell}>
                  复制邀请链接
                </Button>
              </Space>
            </Card>

            {renderInviteStatsCards()}
            {renderReferralFunnel()}
          </Space>
        </Card>
      ),
    },
    {
      key: 'notify',
      label: tabLabel(<BellOutlined />, '通知方式', '通知'),
      children: (
        <Card>
          <Space direction="vertical" size="large" style={{ width: '100%', maxWidth: 500 }}>
            <div>
              <Space>
                <Text>邮件通知</Text>
                <Switch checked={emailNotify} onChange={setEmailNotify} />
              </Space>
            </div>
            <div>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Webhook URL</Text>
              <Input value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} placeholder="https://example.com/webhook" />
            </div>
            <Button type="primary" onClick={saveNotification} loading={savingNotify}>保存设置</Button>
          </Space>
        </Card>
      ),
    },
    {
      key: 'tasks',
      label: tabLabel(<TrophyOutlined />, '我的任务', '任务'),
      children: (
        <div>
          {myTasks.length === 0 && !myTasksLoading ? (
            <Empty description="暂未参与任何任务" />
          ) : (
            <Table
              loading={myTasksLoading}
              dataSource={myTasks}
              rowKey="id"
              size="small"
              pagination={false}
              columns={[
                { title: '任务', dataIndex: 'title', key: 'title', ellipsis: true },
                { title: '金币', dataIndex: 'reward_coins', key: 'reward', width: 80,
                  render: (v: number) => <Text style={{ color: '#C5A55A' }}>{v}</Text> },
                { title: '状态', dataIndex: 'status', key: 'status', width: 80,
                  render: (v: string) => {
                    const sc: Record<string, { color: string; label: string }> = {
                      published: { color: 'blue', label: '招募中' },
                      assigned: { color: 'cyan', label: '已指派' },
                      in_progress: { color: 'orange', label: '执行中' },
                      submitted: { color: 'purple', label: '已提交' },
                      completed: { color: 'green', label: '已完成' },
                      cancelled: { color: 'default', label: '已取消' },
                    };
                    const c = sc[v] || { color: 'default', label: v };
                    return <Tag color={c.color}>{c.label}</Tag>;
                  },
                },
                { title: '我的申请', key: 'myapp', width: 80,
                  render: (_: any, r: any) => {
                    const s = r.my_application?.status;
                    if (!s) return '-';
                    const m: Record<string, { color: string; text: string }> = {
                      pending: { color: 'orange', text: '待审' },
                      approved: { color: 'green', text: '通过' },
                      rejected: { color: 'red', text: '未通过' },
                      withdrawn: { color: 'default', text: '已撤回' },
                    };
                    const c = m[s] || { color: 'default', text: s };
                    return <Tag color={c.color}>{c.text}</Tag>;
                  },
                },
                { title: '截止', dataIndex: 'deadline', key: 'deadline', width: 100,
                  render: (v: string) => v ? formatCN(v, 'MM-DD') : '-' },
              ]}
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: isPhone ? 12 : 16, paddingBottom: isMobileShell ? 24 : 16 }}>
      <Tabs
        defaultActiveKey="account"
        items={tabItems}
        onChange={(key) => { if (key === 'tasks') fetchMyTasks(); }}
        tabBarGutter={isPhone ? 12 : 24}
      />
    </div>
  );
};

export default PersonalAccount;
